import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { isTaskAssignedToStudent, type Task } from "../lib/taskEngine";
import type { ClassroomMember } from "../lib/classroomEngine";

function isTaskActive(task: Task): boolean {
  return !task.expiresAt || Date.now() <= task.expiresAt;
}

export function useStudentTasks(
  studentId: string | undefined,
  memberships: ClassroomMember[]
) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!studentId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const classroomCodes = [...new Set(memberships.map((m) => m.classroomCode))];
    const taskMap = new Map<string, Task>();
    const unsubs: (() => void)[] = [];

    const publish = () => {
      const merged = Array.from(taskMap.values())
        .filter(isTaskActive)
        .filter((t) => isTaskAssignedToStudent(t, studentId))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setTasks(merged);
      setLoading(false);
    };

    setLoading(true);

    for (const classroomCode of classroomCodes) {
      const q = query(
        collection(db, "bordona_tasks"),
        where("classroomId", "==", classroomCode),
        orderBy("createdAt", "desc")
      );
      unsubs.push(
        onSnapshot(
          q,
          (snap) => {
            for (const id of [...taskMap.keys()]) {
              const t = taskMap.get(id);
              if (t?.classroomId === classroomCode) taskMap.delete(id);
            }
            snap.docs.forEach((d) => {
              taskMap.set(d.id, { id: d.id, ...d.data() } as Task);
            });
            publish();
          },
          (err) => {
            setError(err.message);
            setLoading(false);
          }
        )
      );
    }

    const directQ = query(
      collection(db, "bordona_tasks"),
      where("assignedStudentIds", "array-contains", studentId),
      orderBy("createdAt", "desc")
    );
    unsubs.push(
      onSnapshot(
        directQ,
        (snap) => {
          snap.docs.forEach((d) => {
            taskMap.set(d.id, { id: d.id, ...d.data() } as Task);
          });
          publish();
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      )
    );

    if (classroomCodes.length === 0) {
      publish();
    }

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [studentId, memberships]);

  return { tasks, loading, error };
}
